import { useQueryClient } from '@tanstack/react-query';
import { Popconfirm } from 'antd';
import { useState } from 'react';

import { useNotify } from '@/api/notify';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { Button, Card, EmptyState, SectionTitle } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';
import { formatAmount } from '@core/format/money';

import {
  cancelCompany,
  platformKeys,
  reactivateCompany,
  setGraceDays,
  suspendCompany,
  useTenantBilling,
  voidPayment,
  type SubscriptionPayment,
  type TenantBilling,
} from './api';
import {
  describeTerms,
  kindLabels,
  maxGraceDays,
  methodLabels,
  modelLabels,
  standingSentence,
  standingWords,
  todayIso,
} from './billing';

/**
 * What the company pays and has paid (docs/Platform_Billing_Design.md §8): its standing in one
 * word and one sentence, its terms, and the writes a super-admin may make. Setting terms and
 * recording a payment are pages — the insert/update rule — so they are buttons here; changing grace,
 * suspending and taking notice are one value or none, so they confirm in place.
 */
export function BillingSection({
  tenantId,
  canWrite,
  onSetTerms,
  onRecordPayment,
  onReceipt,
}: {
  tenantId: number;
  canWrite: boolean;
  onSetTerms: () => void;
  onRecordPayment: () => void;
  onReceipt: (paymentId: number) => void;
}) {
  const billing = useTenantBilling(tenantId);
  const data = billing.data;

  return (
    <>
      <Card>
        <SectionTitle
          icon="icon icon-badge-indian-rupee"
          hue="teal"
          trailing={
            data && (
              <StatusBadge tone={standingWords[data.standing].tone}>
                {standingWords[data.standing].label}
              </StatusBadge>
            )
          }
        >
          Billing
        </SectionTitle>

        {data && (
          <>
            <p className="text-sm text-gray-900">{standingSentence(data, todayIso())}</p>

            <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <Fact label="Terms">
                {data.terms ? `${modelLabels[data.terms.chargingModel]} · ` : ''}
                {describeTerms(data)}
              </Fact>
              <Fact label="Paid until">{data.paidUntil ? formatDate(data.paidUntil) : '—'}</Fact>
              {data.terms?.isNegotiated && data.terms.priceNote && (
                <Fact label="Why this price">{data.terms.priceNote}</Fact>
              )}
              <Fact label="Grace">
                {data.graceDays} day{data.graceDays === 1 ? '' : 's'} after it falls due
              </Fact>
              {data.cancelledOn && (
                <Fact label="Notice given">
                  {formatDate(data.cancelledOn)}
                  {data.cancellationReason ? ` — ${data.cancellationReason}` : ''}
                </Fact>
              )}
            </dl>

            {canWrite && <BillingActions billing={data} {...{ onSetTerms, onRecordPayment }} />}
          </>
        )}
      </Card>

      {data && (
        <PaymentHistory
          billing={data}
          canWrite={canWrite}
          tenantId={tenantId}
          onReceipt={onReceipt}
        />
      )}
    </>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-default text-[length:var(--scale-field-hint)]">{label}</dt>
      <dd className="text-gray-900">{children}</dd>
    </div>
  );
}

function useBillingWrite(tenantId: number) {
  const notify = useNotify();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const run = async (write: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await write();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: platformKeys.billing(tenantId) }),
        queryClient.invalidateQueries({ queryKey: platformKeys.companies }),
      ]);
      notify.success(success);
    } catch (error) {
      notify.error(error);
    } finally {
      setBusy(false);
    }
  };

  return { busy, run };
}

function BillingActions({
  billing,
  onSetTerms,
  onRecordPayment,
}: {
  billing: TenantBilling;
  onSetTerms: () => void;
  onRecordPayment: () => void;
}) {
  const { busy, run } = useBillingWrite(billing.tenantId);
  const suspended = billing.tenantStatus === 'Suspended';

  return (
    <div className="border-border-color mt-4 flex flex-wrap gap-2 border-t pt-4">
      {/* A payment needs terms to belong to — the server's TermsRequired, offered as no button. */}
      {billing.terms && (
        <Button variant="primary" icon="icon icon-receipt" onClick={onRecordPayment}>
          Record payment
        </Button>
      )}
      <Button
        variant={billing.terms ? 'secondary' : 'primary'}
        icon="icon icon-file-pen"
        onClick={onSetTerms}
      >
        {billing.terms ? 'Change terms' : 'Set terms'}
      </Button>
      {billing.terms && (
        <GraceAction
          current={billing.graceDays}
          busy={busy}
          onSave={(days) =>
            void run(() => setGraceDays(billing.tenantId, days), `Grace set to ${days} days.`)
          }
        />
      )}
      {suspended ? (
        <Popconfirm
          title="Lift the suspension?"
          description="Access goes back to whatever the dates say. It pays for nothing."
          okText="Reactivate"
          okButtonProps={{ loading: busy }}
          cancelText="Cancel"
          onConfirm={() =>
            void run(() => reactivateCompany(billing.tenantId), 'Suspension lifted.')
          }
        >
          <Button disabled={busy} icon="icon icon-lock-open">
            Reactivate
          </Button>
        </Popconfirm>
      ) : (
        <Popconfirm
          title="Suspend this company?"
          description="Nobody can sign in to it until it is reactivated. Its data is kept."
          okText="Suspend"
          okButtonProps={{ danger: true, loading: busy }}
          cancelText="Cancel"
          onConfirm={() => void run(() => suspendCompany(billing.tenantId), 'Company suspended.')}
        >
          <Button variant="danger" disabled={busy} icon="icon icon-lock">
            Suspend
          </Button>
        </Popconfirm>
      )}
      {billing.terms && !billing.cancelledOn && (
        <ReasonAction
          label="Record notice"
          title="Has this company given notice?"
          hint="It keeps working until the date it has paid for, then reads only. Nothing is deleted."
          okText="Record notice"
          busy={busy}
          onConfirm={(reason) =>
            void run(() => cancelCompany(billing.tenantId, reason), 'Notice recorded.')
          }
        />
      )}
    </div>
  );
}

/**
 * Grace is how patient the platform is with one shop, not part of what it agreed to pay — so it is
 * edited in place on the current terms, one number anchored to its button, rather than a page.
 */
function GraceAction({
  current,
  busy,
  onSave,
}: {
  current: number;
  busy: boolean;
  onSave: (days: number) => void;
}) {
  const [typed, setTyped] = useState(String(current));
  const days = Number(typed);
  const valid = typed.trim() !== '' && Number.isInteger(days) && days >= 0 && days <= maxGraceDays;

  return (
    <Popconfirm
      title="Days of grace"
      description={
        <div className="w-56 pt-1">
          <p className="text-default mb-1.5 text-[12px]">
            Full access this many days after it falls due, then read-only. 0 to {maxGraceDays}.
          </p>
          <input
            type="number"
            min={0}
            max={maxGraceDays}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            onWheel={(event) => event.currentTarget.blur()}
            className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
          />
        </div>
      }
      okText="Save"
      okButtonProps={{ loading: busy, disabled: !valid }}
      cancelText="Cancel"
      onOpenChange={(open) => open && setTyped(String(current))}
      onConfirm={() => onSave(days)}
      placement="bottomLeft"
    >
      <Button disabled={busy} icon="icon icon-hourglass">
        Grace
      </Button>
    </Popconfirm>
  );
}

/**
 * A write with a compulsory reason, anchored to its button — the expense voucher's `VoidAction`
 * shape, for the reason given there: a cell that grows cannot reflow under `text-nowrap`.
 */
function ReasonAction({
  label,
  title,
  hint,
  okText,
  busy,
  danger = false,
  onConfirm,
}: {
  label: string;
  title: string;
  hint: string;
  okText: string;
  busy: boolean;
  danger?: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Popconfirm
      title={title}
      description={
        <div className="w-60 pt-1">
          <p className="text-default mb-1.5 text-[12px]">{hint}</p>
          <input
            type="text"
            placeholder="Why?"
            maxLength={512}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="border-border-color h-8 w-full rounded border bg-white px-2 text-[12px] text-gray-900"
          />
        </div>
      }
      okText={okText}
      okButtonProps={{ danger, loading: busy, disabled: reason.trim().length === 0 }}
      cancelText="Cancel"
      onConfirm={() => {
        onConfirm(reason.trim());
        setReason('');
      }}
      placement="leftTop"
    >
      <Button disabled={busy}>{label}</Button>
    </Popconfirm>
  );
}

/** Every payment and every stretch given free, newest first; a voided one stays, struck through. */
function PaymentHistory({
  billing,
  canWrite,
  tenantId,
  onReceipt,
}: {
  billing: TenantBilling;
  canWrite: boolean;
  tenantId: number;
  onReceipt: (paymentId: number) => void;
}) {
  const { busy, run } = useBillingWrite(tenantId);

  const columns: DataViewColumn<SubscriptionPayment>[] = [
    {
      title: 'Receipt',
      key: 'receipt',
      primary: true,
      render: (_, p) => (
        <>
          <span className="block font-mono font-semibold text-gray-900">{p.receiptNumber}</span>
          <span className="text-default block text-[length:var(--scale-field-hint)]">
            {formatDate(p.receivedOn)} · {kindLabels[p.kind]}
            {p.voidedOn && (
              <>
                {' '}
                <StatusBadge tone="negative">Voided</StatusBadge>
              </>
            )}
          </span>
        </>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (_, p) => (
        <span className={p.voidedOn ? 'text-default line-through' : 'font-semibold'}>
          ₹{formatAmount(p.amount)}
        </span>
      ),
    },
    {
      title: 'Covers',
      key: 'covers',
      render: (_, p) =>
        p.coversFrom && p.coversTo
          ? `${formatDate(p.coversFrom)} – ${formatDate(p.coversTo)}`
          : 'No dates',
    },
    {
      title: 'How',
      key: 'method',
      hideOnCard: true,
      render: (_, p) =>
        p.voidedOn ? (
          <span className="text-default">{p.voidReason}</span>
        ) : (
          <>
            {p.method ? methodLabels[p.method] : '—'}
            {p.reference && (
              <span className="text-default block font-mono text-[length:var(--scale-field-hint)]">
                {p.reference}
              </span>
            )}
          </>
        ),
    },
    {
      title: '',
      key: 'actions',
      trailing: true,
      render: (_, p) => (
        <div className="flex justify-end gap-2">
          <Button icon="icon icon-printer" onClick={() => onReceipt(p.id)}>
            Receipt
          </Button>
          {canWrite && !p.voidedOn && (
            <ReasonAction
              label="Void"
              title="Void this receipt?"
              hint="Its number stays in the sequence; the paid-until date falls back to what the other payments cover."
              okText="Void"
              danger
              busy={busy}
              onConfirm={(reason) =>
                void run(() => voidPayment(tenantId, p.id, reason), `${p.receiptNumber} voided.`)
              }
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <Card flush>
      <div className="px-4 pt-4">
        <SectionTitle icon="icon icon-receipt" hue="teal">
          Payments
        </SectionTitle>
      </div>
      <DataView
        columns={columns}
        dataSource={billing.payments}
        rowKey="id"
        empty={
          <EmptyState
            title="Nothing recorded yet"
            hint={
              billing.terms
                ? 'Record the first payment and its receipt is numbered and printed from here.'
                : 'Set the terms first; a payment needs terms to belong to.'
            }
          />
        }
      />
    </Card>
  );
}
